import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSessionExpired } from '@/contexts/SessionExpiredContext';

/**
 * Full-screen overlay that appears when the user's session has expired.
 * Shows a message and automatically redirects to the login page after a brief delay.
 * This prevents users from seeing error states when returning to a stale tab.
 */
export function SessionExpiredOverlay() {
    const { isSessionExpired, setSessionExpired } = useSessionExpired();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!isSessionExpired) return;

        // Redirect to login after a brief delay to show the message
        const timer = setTimeout(() => {
            setSessionExpired(false);
            navigate('/login', {
                state: {
                    from: location,
                    sessionExpired: true,
                },
                replace: true,
            });
        }, 1500);

        return () => clearTimeout(timer);
    }, [isSessionExpired, navigate, location, setSessionExpired]);

    if (!isSessionExpired) return null;

    return (
        <div className="fixed inset-0 z-100 flex items-center justify-center bg-gray-50 dark:bg-darker">
            <div className="text-center">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Session Expired
                </h1>
                <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Redirecting to sign in...
                </p>
            </div>
        </div>
    );
}
